import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import useApi from "../customHooks/useAPI";
import useProfile from "../customHooks/useProfile";

function ProfileForm() {
    const navigate = useNavigate();
    const { doctorId, email, updateUserStatus } = useAuth();
    const { callApi: callSubmitForm } = useApi();
    const { callApi } = useApi();
    const [HospitalDetails, setHospitalDetials] = useState(null);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: email || "",
        phone: "",
        experience: "",
        hospitalID: "",
        degrees: [{ type: "", specialization: "", file: null }],
        proofs: [{ type: "", file: null }],
        licenses: [{ name: "", file: null }],
    });

    // Helper functions
    const addItem = (field, item) => {
        setFormData((prev) => ({
            ...prev,
            [field]: [...prev[field], item],
        }));
    };

    const removeItem = (field, index) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index),
        }));
    };

    const updateField = (field, index, key, value) => {
        setFormData((prev) => {
            const updated = [...prev[field]];
            updated[index][key] = value;
            return { ...prev, [field]: updated };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!HospitalDetails) {
            Swal.fire({
                title: "Error!",
                text: "Fetch hospital detials before submitting.",
                icon: "error",
            });
            return;
        }
        try {
            // ✅ Step 1: Update basic doctor details
            const doctorDetails = {
                id: doctorId,
                first_name: formData.firstName,
                last_name: formData.lastName,
                doctor_details: {
                    yrs_of_exp: parseInt(formData.experience),
                    phone_number: formData.phone
                },
                hospital_id: formData.hospitalID,
                status: "IN_REVIEW",
            };

            await callSubmitForm({
                url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/updateDoctor`,
                method: "POST",
                headers: {
                    "Doctor-Id": doctorId.toString(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(doctorDetails),
            });

            console.log("✅ Doctor details updated");

            // ✅ Step 2: Define upload function
            const uploadDocument = async (file, type, name, specialization = "") => {
                if (!file) return;

                const formdata = new FormData();
                formdata.append("file", file);
                formdata.append(
                    "doctorDocument",
                    JSON.stringify({
                        type,
                        document_name: name,
                        specialization,
                    })
                );

                await callSubmitForm({
                    url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/addDocument`,
                    method: "POST",
                    headers: {
                        "Doctor-Id": doctorId.toString(),
                    },
                    body: formdata,
                });

                console.log(`✅ Uploaded ${type}: ${name}`);
            };

            // ✅ Step 3: Collect all upload promises
            const uploadTasks = [];

            // Degrees
            formData.degrees.forEach((deg) =>
                uploadTasks.push(uploadDocument(deg.file, "DEGREE", deg.type, deg.specialization))
            );

            // Proofs
            formData.proofs.forEach((prf) =>
                uploadTasks.push(uploadDocument(prf.file, "PERSONAL_ID", prf.type))
            );

            // Licenses
            formData.licenses.forEach((lic) =>
                uploadTasks.push(uploadDocument(lic.file, "LICENCE", lic.name))
            );

            // ✅ Step 4: Wait for all uploads (fail fast if any one fails)
            await Promise.all(uploadTasks);

            // ✅ Step 5: Success flow
            Swal.fire({
                title: "Good job!",
                text: "Profile submitted successfully!",
                icon: "success",
            });

            updateUserStatus("IN_REVIEW");
            navigate("/profilepending");
        } catch (error) {
            console.error("❌ Error submitting form:", error);
            Swal.fire({
                title: "Error!",
                text: error.message || "Failed to submit profile. Please try again.",
                icon: "error",
            });
        }
    };

    const fetchHospital = async() => {
        if (formData.hospitalID=="") return;
        const responce = await callApi({
            url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/common/getHospitalUsingId?id=${formData.hospitalID}`,
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Doctor-Id": doctorId.toString(),
            },
        });
        setHospitalDetials(responce.data);
    }

    return (
        <div className="pt-16 flex justify-center h-full bg-gradient-to-br from-green-50 to-blue-50 py-10">
            <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-2xl h-fit">
                <h1 className="text-3xl font-bold text-gray-800 mb-2 text-center">
                    Complete Your Profile
                </h1>
                <p className="text-gray-500 mb-6 text-center">
                    Please provide your details for verification
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <input
                            type="text"
                            placeholder="First Name*"
                            required
                            className="border p-3 rounded-lg w-full"
                            value={formData.firstName}
                            onChange={(e) =>
                                setFormData({ ...formData, firstName: e.target.value })
                            }
                        />
                        <input
                            type="text"
                            placeholder="Last Name*"
                            required
                            className="border p-3 rounded-lg w-full"
                            value={formData.lastName}
                            onChange={(e) =>
                                setFormData({ ...formData, lastName: e.target.value })
                            }
                        />
                    </div>

                    <input
                        type="email"
                        placeholder="Email*"
                        required
                        className="border p-3 rounded-lg w-full"
                        value={formData.email}
                        readOnly
                    />

                    <input
                        type="tel"
                        placeholder="Phone Number"
                        className="border p-3 rounded-lg w-full"
                        value={formData.phone}
                        onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                        }
                    />

                    <input
                        type="number"
                        placeholder="Years of Experience*"
                        required
                        className="border p-3 rounded-lg w-full"
                        value={formData.experience}
                        min={0}
                        max={100}
                        onChange={(e) =>
                            setFormData({ ...formData, experience: e.target.value })
                        }
                    />
                    
                    <div className="space-y-4">
                        {/* Row: Input + Button */}
                        <div className="flex gap-3">
                            <input
                                type="number"
                                placeholder="Hospital ID*"
                                required
                                min={0}
                                max={100}
                                className="flex-1 border p-3 rounded-lg"
                                value={formData.hospitalID}
                                onChange={(e) =>
                                    setFormData({ ...formData, hospitalID: e.target.value })
                                }
                            />

                            <button
                                type="button"
                                className="px-6 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                                onClick={fetchHospital}
                            >
                                Fetch Hospital
                            </button>
                        </div>

                        {/* Result: Hospital Name */}
                        {HospitalDetails && (
                            <div className="p-3 border rounded-lg bg-gray-50">
                                <span className="text-sm text-gray-500">Hospital Name</span>
                                <p className="text-lg font-semibold text-gray-800">
                                    {HospitalDetails.name}
                                    <br/>
                                    {HospitalDetails.address}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Degrees Section */}
                    <div>
                        <h2 className="font-semibold mb-2">Degrees</h2>
                        {formData.degrees.map((deg, i) => (
                            <div key={i} className="space-y-2 mb-4 border p-3 rounded-lg">
                                <select
                                    required
                                    className="border p-2 rounded-lg w-full"
                                    value={deg.type}
                                    onChange={(e) =>
                                        updateField("degrees", i, "type", e.target.value)
                                    }
                                >
                                    <option value="">Select Degree</option>
                                    <option value="MBBS">MBBS</option>
                                    <option value="MD">MD</option>
                                    <option value="MS">MS</option>
                                    <option value="BDS">BDS</option>
                                    <option value="MDS">MDS</option>
                                    <option value="Other">Other</option>
                                </select>

                                <input
                                    type="text"
                                    placeholder="Specialization"
                                    className="border p-2 rounded-lg w-full"
                                    value={deg.specialization}
                                    onChange={(e) =>
                                        updateField("degrees", i, "specialization", e.target.value)
                                    }
                                />

                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    className="w-full"
                                    onChange={(e) =>
                                        updateField("degrees", i, "file", e.target.files[0])
                                    }
                                />

                                {formData.degrees.length > 1 && (
                                    <button
                                        type="button"
                                        className="text-red-500 text-sm"
                                        onClick={() => removeItem("degrees", i)}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            className="text-blue-600 text-sm"
                            onClick={() =>
                                addItem("degrees", { type: "", specialization: "", file: null })
                            }
                        >
                            + Add Degree
                        </button>
                    </div>

                    {/* Proof of ID */}
                    <div>
                        <h2 className="font-semibold mb-2">Proof of Identification</h2>
                        {formData.proofs.map((prf, i) => (
                            <div key={i} className="space-y-2 mb-4 border p-3 rounded-lg">
                                <select
                                    required
                                    className="border p-2 rounded-lg w-full"
                                    value={prf.type}
                                    onChange={(e) =>
                                        updateField("proofs", i, "type", e.target.value)
                                    }
                                >
                                    <option value="">Select ID</option>
                                    <option value="Aadhar">Aadhar Card</option>
                                    <option value="Driving License">Driving License</option>
                                    <option value="PAN">PAN Card</option>
                                    <option value="Voter">Voter Card</option>
                                </select>

                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    className="w-full"
                                    onChange={(e) =>
                                        updateField("proofs", i, "file", e.target.files[0])
                                    }
                                />
                            </div>
                        ))}
                    </div>

                    {/* Licenses */}
                    <div>
                        <h2 className="font-semibold mb-2">Licenses</h2>
                        {formData.licenses.map((lic, i) => (
                            <div key={i} className="space-y-2 mb-4 border p-3 rounded-lg">
                                <input
                                    type="text"
                                    placeholder="License Name"
                                    required
                                    className="border p-2 rounded-lg w-full"
                                    value={lic.name}
                                    onChange={(e) =>
                                        updateField("licenses", i, "name", e.target.value)
                                    }
                                />
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    className="w-full"
                                    onChange={(e) =>
                                        updateField("licenses", i, "file", e.target.files[0])
                                    }
                                />

                                {formData.licenses.length > 1 && (
                                    <button
                                        type="button"
                                        className="text-red-500 text-sm"
                                        onClick={() => removeItem("licenses", i)}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            className="text-blue-600 text-sm"
                            onClick={() => addItem("licenses", { name: "", file: null })}
                        >
                            + Add License
                        </button>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                    >
                        Submit for Verification
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ProfileForm;
